/* @jsx jsx */

/** @jsxFrag React.Fragment */
import { css, jsx } from '@emotion/react';

import * as React from 'react';

import { useMutation } from '@apollo/client';

import user from 'js/lib/user';

import { InlineNotification, StandaloneCheckbox, Typography2 } from '@coursera/cds-core';
import { useTracker, useVisibilityTracker } from '@coursera/event-pulse/react';

import { receivedEmailPreferences } from 'bundles/account-settings/actions/SettingsServerActionCreators';
import { UpdateStates } from 'bundles/account-settings/constants/SettingsConstants';
import type { UpdateGlobalOptOutMutationVariables } from 'bundles/account-settings/queries';
import { UpdateGlobalOptOutMutation } from 'bundles/account-settings/queries';
import SettingsStore from 'bundles/account-settings/stores/SettingsStore';
import type { UpdateStatus } from 'bundles/account-settings/types/EmailPreferences';
import useDebounce from 'bundles/common/hooks/useDebounce';
import { getStatusMessages } from 'bundles/user-account/utils/errorMessageUtils';

import _t from 'i18n!nls/account-settings';

type Props = {
  preference?: boolean;
  onChange: (value: boolean) => void;
};

const styles = {
  title: css`
    border: 0;
    margin-block-end: var(--cds-spacing-100);
  `,
  supportText: css`
    margin-block-end: var(--cds-spacing-200);
  `,
  inlineNotification: css`
    margin-block-start: var(--cds-spacing-200);
  `,
};

const GlobalEmailNotificationPreference: React.FC<Props> = ({ onChange, preference = false }) => {
  const userId = user.get().id.toString();
  const title = _t('Email notifications');
  const supportText = _t(
    'You can select the type of email notifications you want to opt in and out of sent by Coursera.'
  );
  const [isOptedOut, setIsOptedOut] = React.useState<boolean>(() => preference);
  const [preferenceUpdateStatus, setPreferenceUpdateStatus] = React.useState<UpdateStatus>({
    state: UpdateStates.IDLE,
  });

  const track = useTracker();
  const checkboxRef: React.MutableRefObject<HTMLDivElement | null> = useVisibilityTracker('view_checkbox', {
    checkbox: {
      name: 'global_email_notifications_preference',
      checked: isOptedOut,
    },
  });

  const [updateEmailGlobalPreferences] = useMutation<$TSFixMe, UpdateGlobalOptOutMutationVariables>(
    UpdateGlobalOptOutMutation,
    {
      onCompleted: (data) => {
        const global = data?.UpdateGlobalOptOut?.global;
        const notificationPreferences = SettingsStore.getNotificationPreferences();
        const emailPreferences = SettingsStore.getEmailPreferences();

        setPreferenceUpdateStatus({ state: UpdateStates.SUCCEEDED });
        receivedEmailPreferences({ ...notificationPreferences, global }, emailPreferences);
      },
      onError: () => {
        setPreferenceUpdateStatus({ state: UpdateStates.FAILED });
        onChange(preference);
        setIsOptedOut(preference);
      },
    }
  );

  const toggleEmailPreferences = useDebounce(() => {
    updateEmailGlobalPreferences({ variables: { userId, body: {}, enabled: !isOptedOut } });
  }, 500);

  const handleToggleEmailPreferences = () => {
    onChange(!isOptedOut);
    setIsOptedOut((prevOptedOut) => !prevOptedOut);
    track('click_checkbox', {
      checkbox: {
        name: 'global_email_notifications_preference',
        checked: !isOptedOut,
      },
    });
    toggleEmailPreferences();
  };

  return (
    <fieldset aria-label={`${title}. ${supportText}`}>
      <Typography2 component="legend" variant="subtitleLarge" css={styles.title}>
        {title}
      </Typography2>
      <Typography2 component="p" css={styles.supportText}>
        {supportText}
      </Typography2>
      <StandaloneCheckbox
        ref={checkboxRef}
        optional
        label={_t('Opt-out of all Coursera emails')}
        checked={isOptedOut}
        onChange={handleToggleEmailPreferences}
      />
      <div aria-live="polite">
        {preferenceUpdateStatus.state === UpdateStates.FAILED && (
          <InlineNotification css={styles.inlineNotification} severity="error">
            {getStatusMessages.UPDATE_FAILED}
          </InlineNotification>
        )}

        {preferenceUpdateStatus.state === UpdateStates.SUCCEEDED && (
          <InlineNotification css={styles.inlineNotification} severity="success">
            {getStatusMessages.UPDATE_SUCCEEDED}
          </InlineNotification>
        )}
      </div>
    </fieldset>
  );
};

export default GlobalEmailNotificationPreference;
